import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { GraduationCap, Loader2, Shield } from 'lucide-react';
import { UserRole, supabase } from '@/lib/supabase';

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<UserRole>('studente');
  const [loading, setLoading] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [privacyText, setPrivacyText] = useState('');
  const [loadingPrivacy, setLoadingPrivacy] = useState(true);
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    setLoadingPrivacy(true);
    const { data, error } = await supabase
      .from('privacy_settings')
      .select('privacy_policy, company_name, responsible_name, vat_number, legal_address, postal_code, city, phone, contact_email')
      .maybeSingle();

    if (!error && data) {
      let fullText = data.privacy_policy || 'Privacy policy non disponibile.';

      if (data.company_name || data.responsible_name || data.vat_number || data.legal_address || data.phone || data.contact_email) {
        fullText = `TITOLARE DEL TRATTAMENTO DATI\n\n`;
        if (data.company_name) fullText += `Società: ${data.company_name}\n`;
        if (data.responsible_name) fullText += `Responsabile: ${data.responsible_name}\n`;
        if (data.vat_number) fullText += `Partita IVA: ${data.vat_number}\n`;
        if (data.legal_address) {
          fullText += `Sede Legale: ${data.legal_address}`;
          if (data.postal_code) fullText += `, ${data.postal_code}`;
          if (data.city) fullText += ` ${data.city}`;
          fullText += `\n`;
        }
        if (data.phone) fullText += `Telefono: ${data.phone}\n`;
        if (data.contact_email) fullText += `Email per richieste e reclami: ${data.contact_email}\n`;
        fullText += `\n${'='.repeat(60)}\n\n${data.privacy_policy || ''}`;
      }

      setPrivacyText(fullText);
    } else {
      setPrivacyText('Privacy policy non ancora configurata. Contatta l\'amministratore.');
    }
    setLoadingPrivacy(false);
  };

  const handlePrivacyCheckboxClick = () => {
    if (!privacyAccepted) {
      setShowPrivacyDialog(true);
    } else {
      setPrivacyAccepted(false);
    }
  };

  const handleAcceptPrivacy = () => {
    setPrivacyAccepted(true);
    setShowPrivacyDialog(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!privacyAccepted) {
      toast({
        title: 'Privacy richiesta',
        description: 'Devi accettare la privacy policy per continuare',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    const { error } = await signUp(email, password, fullName, role);

    if (error) {
      toast({
        title: 'Errore',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Registrazione completata',
        description: 'Account creato con successo! Effettua il login.',
      });
      navigate('/login');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2 text-center">
          <div className="mx-auto w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mb-2">
            <GraduationCap className="w-7 h-7 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Registrati</CardTitle>
          <CardDescription>
            Crea un account per iniziare a imparare o insegnare
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Nome completo</Label>
              <Input
                id="fullName"
                type="text"
                placeholder="Mario Rossi"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                minLength={6}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="role">Tipo di account</Label>
              <Select value={role} onValueChange={(value) => setRole(value as UserRole)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona ruolo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="studente">Studente</SelectItem>
                  <SelectItem value="docente">Docente</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-start space-x-3 rounded-lg border border-gray-200 p-4 bg-gray-50">
              <Checkbox
                id="privacy"
                checked={privacyAccepted}
                onCheckedChange={handlePrivacyCheckboxClick}
                disabled={loading || loadingPrivacy}
              />
              <div className="space-y-1 leading-none">
                <label
                  htmlFor="privacy"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Accetto la privacy policy
                </label>
                <p className="text-xs text-gray-600">
                  Clicca per leggere e accettare la nostra informativa sulla privacy e il trattamento dei dati personali
                </p>
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading || !privacyAccepted}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrazione...
                </>
              ) : (
                'Registrati'
              )}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <span className="text-muted-foreground">Hai già un account? </span>
            <Link to="/login" className="text-blue-600 hover:underline font-medium">
              Accedi
            </Link>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showPrivacyDialog} onOpenChange={setShowPrivacyDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <DialogTitle>Privacy Policy</DialogTitle>
                <DialogDescription>
                  Leggi attentamente la nostra informativa sulla privacy
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <ScrollArea className="h-[400px] w-full rounded-md border p-4">
            <div className="text-sm whitespace-pre-wrap leading-relaxed">
              {privacyText}
            </div>
          </ScrollArea>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowPrivacyDialog(false)}
            >
              Rifiuta
            </Button>
            <Button onClick={handleAcceptPrivacy} className="bg-blue-600 hover:bg-blue-700">
              <Shield className="w-4 h-4 mr-2" />
              Accetto la Privacy Policy
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
